import React, {useEffect} from 'react';
import {
  Link as RouterLink,
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {Box, Link} from '@mui/material';
import {Body} from '@lightricks/react-design-system';
import PoppaysLoader from '@/components/poppays-loader';
import useParams from '@/hooks/use-params';
import translate from '@/utils/translate';
import GigLoadError from '../components/GigLoadError';
import GigShell from '../shell/GigShell';
import {GigTab} from '../shell/gigTabs';
import useGigShellData from '../shell/useGigShellData';
import {captureDeliverablesError, trackDeliverables} from '../telemetry';
import CopyableValue from '../brief/CopyableValue';
import PrepStatusCard from './PrepStatusCard';
import TrackingInfo from './TrackingInfo';
import {FULFILLED_STATE, PrepKind, prepStatusPresentation} from './prepStatus';
import {brandLinkButtonSx, brandLinkSx} from './prepStyles';
import styles from './PrepPage.module.scss';

const KIND_BY_SEGMENT: Record<string, PrepKind> = {
  'shipping': 'shipping',
  'gift-code': 'gift-code',
};

export default function PrepPage() {
  const params = useParams();
  const {gigId} = params;
  const [, segment] = (params['*'] ?? '').split('/');
  const kind = KIND_BY_SEGMENT[segment] ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    shell,
    prep,
    deliverables,
    totals,
    error,
    gigLoading,
    gigFailed,
    submissionsLoading,
    submissionsFailed,
    refetch,
  } = useGigShellData(gigId);

  useEffect(() => {
    trackDeliverables('Opened gig prep', {gigId, kind: kind ?? 'index'});
  }, [gigId, kind]);

  useEffect(() => {
    if (gigFailed || submissionsFailed) {
      captureDeliverablesError(error, 'Gig prep failed to load', {
        gigId,
        kind,
        gigFailed,
        submissionsFailed,
      });
    }
  }, [error, gigFailed, submissionsFailed, gigId, kind]);

  if (gigLoading) {
    return <PoppaysLoader />;
  }

  if (gigFailed) {
    return <GigLoadError onRetry={refetch} />;
  }

  if (!kind) {
    if (prep.shipment) {
      return <Navigate to={`/gigs/${gigId}/prep/shipping`} replace />;
    }
    if (prep.giftCode) {
      return <Navigate to={`/gigs/${gigId}/prep/gift-code`} replace />;
    }
    return <Navigate to={`/gigs/${gigId}`} replace />;
  }

  const handleSelectKind = (tab: GigTab) => {
    if (tab.to) {
      navigate(tab.to);
    }
  };

  const openMessages = () => {
    if (!shell.conversationId) return;
    trackDeliverables('Opened messages from gig prep', {gigId, kind});
    const next = new URLSearchParams(searchParams);
    next.set('conversationId', shell.conversationId);
    setSearchParams(next, {replace: true});
  };

  const shipment = kind === 'shipping' ? prep.shipment : null;
  const giftCode = kind === 'gift-code' ? prep.giftCode : null;
  const record = shipment ?? giftCode;
  const presentation = record
    ? prepStatusPresentation(kind, record.state)
    : undefined;
  const isFulfilled = record?.state === FULFILLED_STATE;

  const messageAction = (label: string) =>
    shell.conversationId ? (
      <Box
        component="button"
        type="button"
        sx={brandLinkButtonSx()}
        data-testid="prep-send-message"
        onClick={openMessages}
      >
        {label}
      </Box>
    ) : null;

  let details: React.ReactNode = null;
  if (shipment?.trackingCode) {
    details = (
      <TrackingInfo shipment={shipment} brandName={shell.merchantName} />
    );
  } else if (giftCode?.couponCode) {
    details = (
      <CopyableValue testID="prep-coupon-code" value={giftCode.couponCode} />
    );
  }

  let fulfilledBody: React.ReactNode = null;
  if (isFulfilled && kind === 'shipping') {
    fulfilledBody = (
      <>
        {translate("Your package has arrived, and it's time to")}{' '}
        <Link
          component={RouterLink}
          to={`/gigs/${gigId}`}
          underline="hover"
          sx={brandLinkSx()}
          data-testid="prep-start-gig"
        >
          {translate('get cracking on that gig!')}
        </Link>{' '}
        {translate("If your product hasn't actually been delivered,")}{' '}
        {messageAction(translate('send us a message ASAP.'))}
      </>
    );
  } else if (isFulfilled) {
    fulfilledBody = (
      <>
        {translate(
          "Use the code below to get the product you'll need for your gig. If you aren't sure where to use the code,"
        )}{' '}
        {messageAction(translate('send us a message in the app.'))}
      </>
    );
  }

  return (
    <GigShell
      gigId={gigId}
      activeKind={kind}
      shell={shell}
      deliverables={deliverables}
      totals={totals}
      showProgress={!submissionsLoading && !submissionsFailed}
      onSelectKind={handleSelectKind}
    >
      <Box className={styles.page}>
        {presentation ? (
          <PrepStatusCard presentation={presentation} details={details}>
            {fulfilledBody}
          </PrepStatusCard>
        ) : (
          <Body size="md" className={styles.empty} data-testid="prep-empty">
            {kind === 'shipping'
              ? translate('There is no shipping information for this gig yet.')
              : translate('There is no gift code for this gig yet.')}
          </Body>
        )}
      </Box>
    </GigShell>
  );
}
